import React, { useEffect, useState } from 'react';
import { Form, Modal, Input, Button, Typography, Tag, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
// API
import { EDIT_BRAND_USER } from 'api/users/mutations';
// Types
import { EditAgency, EditAgencyVariables } from 'api/users/types/EditAgency';
import { GetAgencies_getAgencies_entities } from 'api/campaignV2/types/GetAgencies';
import { CampaignLocationInput } from 'api/graphql-global-types';
// Helpers
import { createLocation } from 'helpers/place';
// UI
import { successNotification, errorNotification } from 'ui/Notification';
// Components
import LocationSelect from 'components/common/LocationSelect/LocationSelect';

const { Text } = Typography;

type EditBrandModalProps = {
  brand: GetAgencies_getAgencies_entities | null;
  isVisible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const EditBrandModal: React.FC<EditBrandModalProps> = ({
  brand,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [editAgency, { loading: editLoading }] = useMutation<
    EditAgency,
    EditAgencyVariables
  >(EDIT_BRAND_USER);

  const [companyDomains, setCompanyDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState('');

  useEffect(() => {
    if (brand && isVisible) {
      const locationArray: CampaignLocationInput[] = [];
      if (
        brand.agencyDetails?.city ||
        brand.agencyDetails?.country ||
        brand.agencyDetails?.latitude
      ) {
        const locationParts = [
          brand.agencyDetails?.city,
          brand.agencyDetails?.state,
          brand.agencyDetails?.country,
        ]
          .filter(Boolean)
          .join(', ');

        locationArray.push({
          placeText: locationParts,
          city: brand.agencyDetails?.city || '',
          state: brand.agencyDetails?.state || '',
          country: brand.agencyDetails?.country || '',
          latitude: brand.agencyDetails?.latitude || null,
          longitude: brand.agencyDetails?.longitude || null,
          placeId: '',
        });
      }

      // Load existing company domains
      const existingDomains = (brand.agencyDetails as any)?.companyDomains;
      setCompanyDomains(Array.isArray(existingDomains) ? existingDomains : []);

      const initialValues = {
        firstName: brand.firstName || '',
        lastName: brand.lastName || '',
        email: brand.email || '',
        phoneNumber: brand.phoneNumber || '',
        jobTitle: brand.agencyDetails?.jobTitle || '',
        locations: locationArray,
      };
      form.setFieldsValue(initialValues);
    }
  }, [brand, isVisible, form]);

  const handleAddDomain = (): void => {
    const trimmedDomain = domainInput.trim();
    if (trimmedDomain && !companyDomains.includes(trimmedDomain)) {
      setCompanyDomains([...companyDomains, trimmedDomain]);
      setDomainInput('');
    }
  };

  const handleRemoveDomain = (domainToRemove: string): void => {
    setCompanyDomains(companyDomains.filter((d) => d !== domainToRemove));
  };

  const handleDomainInputKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };

  const handleFormSubmit = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      if (!brand) {
        return;
      }
      const selectedLocation = values.locations?.[0];
      const location = createLocation(
        selectedLocation?.latitude,
        selectedLocation?.longitude
      );

      const input = {
        agencyId: brand.id,
        firstName: values.firstName || null,
        lastName: values.lastName || null,
        email: values.email || null,
        phoneNumber: values.phoneNumber || null,
        city: selectedLocation?.city || null,
        state: selectedLocation?.state || null,
        country: selectedLocation?.country || null,
        location: location,
        jobTitle: values.jobTitle || null,
        companyDomains: (companyDomains.length > 0
          ? companyDomains
          : null) as string[],
      };
      await editAgency({
        variables: {
          input,
        },
      });
      successNotification('Brand updated successfully!');
      form.resetFields();
      setCompanyDomains([]);
      setDomainInput('');
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      errorNotification(
        err?.message || 'Failed to update brand, please try again later'
      );
    }
  };

  const handleModalClose = (): void => {
    form.resetFields();
    setCompanyDomains([]);
    setDomainInput('');
    onClose();
  };

  const handleLocationChange = (
    newLocations: CampaignLocationInput[]
  ): void => {
    if (newLocations.length > 1) {
      form.setFieldsValue({
        locations: [newLocations[newLocations.length - 1]],
      });
    } else {
      form.setFieldsValue({ locations: newLocations });
    }
  };

  return (
    <Modal
      title="Edit Brand"
      visible={isVisible}
      onCancel={handleModalClose}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        name="EditBrandForm"
        onFinish={handleFormSubmit}
      >
        <Form.Item
          name="firstName"
          label={<Text>First Name</Text>}
          rules={[
            {
              required: true,
              message: 'Please enter first name',
            },
          ]}
        >
          <Input placeholder="Enter first name" />
        </Form.Item>

        <Form.Item
          name="lastName"
          label={<Text>Last Name</Text>}
          rules={[
            {
              required: true,
              message: 'Please enter last name',
            },
          ]}
        >
          <Input placeholder="Enter last name" />
        </Form.Item>

        <Form.Item
          name="email"
          label={<Text>Email</Text>}
          rules={[
            {
              required: true,
              message: 'Please enter email',
            },
            {
              type: 'email',
              message: 'Please enter a valid email address',
            },
          ]}
        >
          <Input placeholder="Enter email" />
        </Form.Item>

        <Form.Item name="phoneNumber" label={<Text>Phone Number</Text>}>
          <Input placeholder="Enter phone number" />
        </Form.Item>

        <Form.Item name="jobTitle" label={<Text>Job Title</Text>}>
          <Input placeholder="Enter job title" />
        </Form.Item>

        <Form.Item label={<Text>Company Domains</Text>}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {companyDomains.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {companyDomains.map((domain) => (
                  <Tag
                    key={domain}
                    closable
                    onClose={() => handleRemoveDomain(domain)}
                    style={{ marginBottom: 4 }}
                  >
                    {domain}
                  </Tag>
                ))}
              </div>
            )}
            <Space>
              <Input
                placeholder="Enter domain (e.g., MILLIONS.co)"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyPress={handleDomainInputKeyPress}
                style={{ width: 250 }}
              />
              <Button
                type="dashed"
                onClick={handleAddDomain}
                icon={<PlusOutlined />}
                disabled={!domainInput.trim()}
              >
                Add
              </Button>
            </Space>
          </Space>
        </Form.Item>

        <Form.Item name="locations" label={<Text>Location</Text>}>
          <LocationSelect
            id="brandLocation"
            placeholder="Select location"
            onChange={handleLocationChange}
            value={form.getFieldValue('locations') || []}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={editLoading}
            style={{ marginRight: 8 }}
          >
            Submit
          </Button>
          <Button onClick={handleModalClose}>Cancel</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditBrandModal;
